// Cart-drawer "Complete the look" slider: image slider per card, colour swatches, quick add.
// Products: /recommendations (newest cart item) -> falls back to the catalog; details via /products/<handle>.js.
// The drawer is morphed from server HTML on every cart change (wiping client markup), so a MutationObserver
// repaints from the cached product list. Adds go through the theme's own CartLinesUpdateEvent so the drawer,
// totals, shipping bar and cart bubble all update exactly like the product page's add to cart.
let cartEvents = null;
const eventsReady = import('@shopify/events').then((m) => (cartEvents = m)).catch(() => null);

const esc = (s) => { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; };
const sized = (src, w) => { if (!src) return ''; const u = String(src).replace(/^\/\//, 'https://'); return u + (u.includes('?') ? '&' : '?') + 'width=' + (w || 500); };
const isColor = (name) => /^colou?r$/i.test(name || '');

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cr-track');
    this._onCartUpdate = (event) => {
      Promise.resolve(event.promise).then(() => {
        if (!this.isConnected) return;
        this._last = 0;
        this.load();
      }).catch(() => {});
    };
    eventsReady.then(() => {
      if (this.isConnected && cartEvents) document.addEventListener(cartEvents.StandardEvents.cartLinesUpdate, this._onCartUpdate);
    });
    this.addEventListener('click', (e) => this.onClick(e));
    // mouse drag-to-scroll, free (no snapping); touch uses native scrolling
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      const t = e.target.closest('.cr-track'); if (!t || e.target.closest('button, select, .cr-slides')) return;
      const startX = e.clientX, startLeft = t.scrollLeft; let moved = false;
      const move = (ev) => {
        const dx = ev.clientX - startX;
        if (!moved && Math.abs(dx) > 4) { moved = true; t.classList.add('is-dragging'); }
        if (moved) t.scrollLeft = startLeft - dx;
      };
      const up = () => {
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
        setTimeout(() => t.classList.remove('is-dragging'), 0);
      };
      window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    });
    this._io = new IntersectionObserver((en) => { if (en.some((x) => x.isIntersecting)) this.load(); });
    this._io.observe(this);
    this.source = this.dataset.source || 'recs';
    window.__crLists = window.__crLists || {};
    this._mo = new MutationObserver(() => {
      const t = this.querySelector('.cr-track');
      const l = window.__crLists[this.source];
      if (!this._painting && l && l.length && t && !t.children.length) this.paint(l);
    });
    this._mo.observe(this, { childList: true, subtree: true });
    if (window.__crLists[this.source]) this.paint(window.__crLists[this.source]);
    this.load();
    if (this.source === 'recs') {   // reload when the set of products in the bag changes (not on quantity changes)
      this._colsSig = '';
      const watch = () => { const el = document.querySelector('script[data-cr-cols]'); const s = el ? el.textContent : ''; if (s !== this._colsSig) { const first = this._colsSig === ''; this._colsSig = s; if (!first) { this._last = 0; this.load(); } } };
      this._wo = new MutationObserver(() => { clearTimeout(this._wt); this._wt = setTimeout(watch, 350); });
      this._wo.observe(document.body, { childList: true, subtree: true, characterData: true });
      setTimeout(watch, 0);
    }
  }
  disconnectedCallback() { if (cartEvents && this._onCartUpdate) document.removeEventListener(cartEvents.StandardEvents.cartLinesUpdate, this._onCartUpdate); if (this._io) this._io.disconnect(); if (this._mo) this._mo.disconnect(); if (this._wo) this._wo.disconnect(); }

  money(cents) {
    const sample = this.dataset.moneySample || '';
    const m = sample.match(/\d[\d.,\s  ']*\d/);
    if (!m) return (cents / 100).toFixed(2) + ' ' + this.dataset.currency;
    const num = m[0];
    const dec = /[.,]\d{2}$/.test(num) ? num.slice(-3, -2) : '';
    const thou = (num.match(/[.,\s  ']/g) || []).find((c) => c !== dec) || '';
    const [whole, frac] = (cents / 100).toFixed(2).split('.');
    return sample.replace(num, whole.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (dec ? dec + frac : ''));
  }

  async details(handle) {
    window.__crData = window.__crData || {};
    if (window.__crData[handle]) return window.__crData[handle];
    const r = await fetch('/products/' + handle + '.js');
    if (!r.ok) return null;
    return (window.__crData[handle] = await r.json());
  }

  async load() {
    const now = Date.now();
    if (this._busy || (this._last && now - this._last < 4000)) return;
    this._busy = true;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      if (!cart.item_count) { this.hidden = true; return; }
      const inCart = new Set(cart.items.map((i) => i.product_id));
      if (this.source === 'recs') { await this.loadCategories(cart); this._last = Date.now(); return; }
      const limit = Number(this.dataset.limit) || 8;
      let handles = [];
      if (this.source === 'recent') {
        let seen = [];
        try { seen = JSON.parse(localStorage.getItem('hv_recent') || '[]'); } catch (e) { /* ignore */ }
        const shown = new Set(((window.__crLists || {}).recs || []).map((p) => p.handle));
        handles = seen.filter((h) => h && !shown.has(h));
      } else {
        const r = await fetch('/recommendations/products.json?product_id=' + cart.items[0].product_id + '&limit=' + (limit + 4) + '&intent=related');
        if (r.ok) handles = ((await r.json()).products || []).filter((p) => !inCart.has(p.id)).map((p) => p.handle);
        if (handles.length < 4) {
          const f = await fetch('/products.json?limit=' + (limit + 8));
          if (f.ok) handles = handles.concat(((await f.json()).products || []).filter((p) => !inCart.has(p.id)).map((p) => p.handle));
        }
      }
      handles = [...new Set(handles)].slice(0, limit + 2);
      let list = (await Promise.all(handles.map((h) => this.details(h).catch(() => null))))
        .filter((p) => p && p.available && p.images && p.images.length && !inCart.has(p.id)).slice(0, limit);
      // keep what the shopper already sees: recommendations are seeded by the newest bag item, so a refetch right after
      // an add would swap the list. Retain the earlier list (same order) and only top it up if it has gaps.
      const prev = window.__crLists[this.source];
      if (prev && prev.length) {
        const have = new Set(prev.map((x) => x.id));
        list = prev.concat(list.filter((x) => !have.has(x.id))).slice(0, limit);
      }
      if (!list.length) { this.hidden = true; return; }
      window.__crLists[this.source] = list;
      this.paint(list);
      this.hidden = false;
      this._last = Date.now();
    } catch (e) { /* leave hidden on failure */ }
    finally { this._busy = false; }
  }


  // Category-driven slider: products come from the categories of what is in the bag. A bag in the cart -> bags, with that bag's
  // sub-category selected and all the other bag sub-categories available as chips. Same for dresses, shoes, hats, ...
  async loadCategories(cart) {
    const menuEl = this.querySelector('script[data-cr-menu]'); if (!menuEl) return false;
    const menu = JSON.parse(menuEl.textContent).filter((c) => c.h);
    const colsEl = document.querySelector('script[data-cr-cols]');
    let cols = {}; try { cols = colsEl ? JSON.parse(colsEl.textContent) : {}; } catch (e) { cols = {}; }
    const info = [];
    cart.items.forEach((it) => {
      const c = cols[it.handle] || [];
      for (const cat of menu) {
        const sub = cat.s.find((s) => c.includes(s.h));
        if (sub || c.includes(cat.h)) { info.push({ parent: cat.h, sub: sub ? sub.h : null }); break; }
      }
    });
    if (!info.length) { this.hidden = true; return true; }
    const parents = [...new Set(info.map((i) => i.parent))];
    const chips = [];
    parents.forEach((ph) => {
      const cat = menu.find((c) => c.h === ph);
      if (cat.s.length) { cat.s.forEach((s) => chips.push({ h: s.h, t: s.t })); chips.push({ h: cat.h, t: 'All ' + cat.t }); }
      else chips.push({ h: cat.h, t: cat.t });
    });
    const sig = cart.items.map((i) => i.handle).join('|');
    if (window.__crSig !== sig || !chips.some((c) => c.h === window.__crSel)) { window.__crSig = sig; window.__crSel = info[0].sub || info[0].parent; }
    this._chips = chips;
    this.renderChips();
    await this.showSelected();
    return true;
  }

  renderChips() {
    const box = this.querySelector('.cr-chips'); if (!box) return;
    box.innerHTML = '';
    this._chips.forEach((c) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'cr-chip' + (c.h === window.__crSel ? ' is-on' : '');
      b.dataset.h = c.h; b.textContent = c.t; box.appendChild(b);
    });
    box.hidden = this._chips.length < 2;
    const on = box.querySelector('.is-on'); if (on && on.scrollIntoView) { const l = on.offsetLeft - 8; if (box.scrollLeft > l || box.scrollLeft + box.clientWidth < l + on.offsetWidth) box.scrollLeft = l; }
  }

  async collectionProducts(handle) {
    window.__crColl = window.__crColl || {};
    if (!window.__crColl[handle]) {
      window.__crColl[handle] = (async () => {
        const products = [], seen = new Set();
        for (let page = 1; ; page++) {
          const response = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page);
          if (!response.ok) throw new Error('Category unavailable');
          const batch = (await response.json()).products || [];
          const fresh = batch.filter(p => !seen.has(p.id));
          fresh.forEach(p => { seen.add(p.id); products.push(p); });
          if (batch.length < 250 || !fresh.length) break;
        }
        return products;
      })().catch(error => { delete window.__crColl[handle]; throw error; });
    }
    return window.__crColl[handle];
  }

  async showSelected() {
    const h = window.__crSel, request = this._categoryRequest = (this._categoryRequest || 0) + 1;
    this.hidden = false;
    this.track = this.querySelector('.cr-track');
    this._painting = true;
    this.track.innerHTML = '<p class="cr-msg" role="status">Loading category…</p>';
    this._painting = false;
    try {
      const [products, cart] = await Promise.all([this.collectionProducts(h), fetch('/cart.js').then(r => r.json())]);
      const inCart = new Set(cart.items.map(i => i.product_id));
      const candidates = products.filter(p => !inCart.has(p.id) && (p.variants || []).some(v => v.available));
      // Bound network concurrency, never the number of products in a category.
      const list = new Array(candidates.length); let next = 0;
      await Promise.all(Array.from({ length: Math.min(8, candidates.length) }, async () => {
        while (next < candidates.length) {
          const index = next++; list[index] = await this.details(candidates[index].handle).catch(() => null);
        }
      }));
      if (this._categoryRequest !== request || window.__crSel !== h) return;
      const available = list.filter(p => p && p.available && p.images && p.images.length && !inCart.has(p.id));
      window.__crLists.recs = available; this.paint(available);
      if (!available.length) this.track.innerHTML = '<p class="cr-msg" role="status">No available products in this category.</p>';
      this.track.scrollLeft = 0;
    } catch (error) {
      if (this._categoryRequest === request) {
        window.__crLists.recs = [];
        this.track.innerHTML = '<p class="cr-msg" role="status">This category could not load. Please choose another category.</p>';
      }
    }
  }

  colorSwatches(p) {
    const o = (p.options || []).find((x) => isColor(x.name));
    if (!o) return [];
    const key = 'option' + o.position, seen = {}, out = [];
    (p.variants || []).forEach((v) => {
      const val = v[key]; if (!val || seen[val]) return;
      const src = v.featured_image && v.featured_image.src; seen[val] = 1;
      if (src) out.push({ value: val, src });
    });
    return out;
  }

  paint(products) {
    this._painting = true;
    this.track = this.querySelector('.cr-track') || this.track;
    this.track.innerHTML = '';
    products.forEach((p) => this.track.appendChild(this.card(p)));
    this.track.querySelectorAll('.cr-card').forEach((c) => this.dots(c));
    this._painting = false;
  }

  card(p) {
    const el = document.createElement('div');
    el.className = 'cr-card'; el.dataset.handle = p.handle; el.dataset.id = p.id;
    const url = '/products/' + p.handle;
    const slides = p.images.map((src) => '<a href="' + url + '" class="cr-slide" tabindex="-1"><img src="' + esc(sized(src, 500)) + '" alt="' + esc(p.title) + '" loading="lazy"></a>').join('');
    const sale = p.compare_at_price && p.compare_at_price > p.price;
    el.innerHTML =
      '<div class="cr-media"><div class="cr-slides">' + slides + '</div>' +
      (p.images.length > 1 ? '<button type="button" class="cr-arrow cr-arrow--prev" aria-label="Previous image">&#10094;</button><button type="button" class="cr-arrow cr-arrow--next" aria-label="Next image">&#10095;</button><div class="cr-dots"></div>' : '') +
      '</div><a href="' + url + '" class="cr-title">' + esc(p.title) + '</a>' +
      '<p class="cr-price">' + (sale ? '<s>' + this.money(p.compare_at_price) + '</s>' : '') + this.money(p.price) + '</p>' +
      '<div class="cr-sw"></div><button type="button" class="cr-add">Quick add</button><div class="cr-opts" hidden></div>';
    if (window.__crAddedId === p.id && Date.now() - (window.__crAddedAt || 0) < 4000) { // drawer re-render right after an add: keep the confirmation
      const ab = el.querySelector('.cr-add'); ab.textContent = 'Added \u2713'; ab.classList.add('is-added');
      setTimeout(() => { ab.textContent = 'Quick add'; ab.classList.remove('is-added'); }, 1800);
    }
    const sw = this.colorSwatches(p);
    if (sw.length > 1) {
      const box = el.querySelector('.cr-sw');
      sw.forEach((x, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'cr-swatch' + (i === 0 ? ' is-on' : ''); b.title = x.value; b.dataset.color = x.value; b.dataset.src = x.src;
        b.setAttribute('aria-label', x.value); b.style.backgroundImage = 'url(' + sized(x.src, 80) + ')';
        box.appendChild(b);
      });
      el.dataset.color = sw[0].value;
    }
    return el;
  }

  dots(card) {
    const sl = card.querySelector('.cr-slides'), d = card.querySelector('.cr-dots'); if (!sl || !d) return;
    const n = sl.children.length, many = n > 7; d.innerHTML = '';
    if (many) d.classList.add('is-count'); else for (let i = 0; i < n; i++) d.appendChild(document.createElement('i'));
    const mark = () => {
      const k = Math.round(sl.scrollLeft / (sl.clientWidth || 1));
      if (many) d.textContent = (k + 1) + ' / ' + n; else [...d.children].forEach((x, i) => x.classList.toggle('is-on', i === k));
    };
    sl.addEventListener('scroll', mark, { passive: true }); mark();
  }

  onClick(e) {
    const chip = e.target.closest && e.target.closest('.cr-chip');
    if (chip) { e.preventDefault(); e.stopPropagation(); window.__crSel = chip.dataset.h; this.renderChips(); this.showSelected(); return; }
    const nav = e.target.closest('.cr-nav-btn');
    if (nav) { this.track.scrollBy({ left: Number(nav.dataset.dir) * this.track.clientWidth * 0.9, behavior: 'smooth' }); return; }
    const card = e.target.closest('.cr-card'); if (!card) return;
    const ar = e.target.closest('.cr-arrow');
    if (ar) { const sl = card.querySelector('.cr-slides'); sl.scrollBy({ left: (ar.classList.contains('cr-arrow--next') ? 1 : -1) * sl.clientWidth, behavior: 'smooth' }); return; }
    const sw = e.target.closest('.cr-swatch');
    if (sw) {
      card.querySelectorAll('.cr-card > .cr-sw .cr-swatch').forEach((y) => y.classList.toggle('is-on', y === sw));
      card.dataset.color = sw.dataset.color;
      const first = card.querySelector('.cr-slide img'), sl = card.querySelector('.cr-slides');
      if (first) first.src = sized(sw.dataset.src, 500);
      if (sl) sl.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (e.target.closest('.cr-add')) this.openOpts(card);
  }

  async openOpts(card) {
    const o = card.querySelector('.cr-opts');
    if (!o.hidden) { o.hidden = true; return; }
    o.hidden = false; o.innerHTML = '<p class="cr-msg">Loading…</p>';
    const p = await this.details(card.dataset.handle).catch(() => null);
    if (!p) { o.innerHTML = '<p class="cr-msg">Could not load options.</p>'; return; }
    o.innerHTML = '';
    const go = document.createElement('button'); go.type = 'button'; go.className = 'cr-go'; go.textContent = 'Add to bag'; go.disabled = true;
    const msg = document.createElement('p'); msg.className = 'cr-msg';
    // nothing to choose: add straight away
    if (p.variants.length === 1 || p.options.every((x) => x.values.length === 1)) { o.append(msg); this.add(card, p, p.variants[0].id, go, msg); return; }
    const chosen = p.options.map(() => ''), groups = [];
    let vid = null;
    const match = (vals) => p.variants.filter((v) => vals.every((x, i) => !x || v['option' + (i + 1)] === x));
    let adding = false;
    const refresh = (auto) => {
      groups.forEach((g, i) => [...g.children].forEach((b) => {
        const test = chosen.slice(); test[i] = b.dataset.v;
        const ok = match(test).some((v) => v.available);
        b.classList.toggle('is-on', chosen[i] === b.dataset.v);
        if (b.classList.contains('cr-pill')) b.disabled = !ok; else b.style.opacity = ok ? '' : '.35';
      }));
      if (!chosen.every(Boolean)) { go.disabled = true; vid = null; msg.textContent = ''; return; }
      const m = match(chosen)[0];
      if (!m || !m.available) { go.disabled = true; vid = null; msg.textContent = m ? 'Sold out' : 'Unavailable'; return; }
      msg.textContent = ''; vid = m.id; go.disabled = false;
      // last choice made: add immediately (no separate "Add to bag" step)
      if (auto && !adding) { adding = true; this.add(card, p, vid, go, msg).finally(() => { adding = false; }); }
    };
    p.options.forEach((op, i) => {
      const color = isColor(op.name), pre = color && card.dataset.color && op.values.includes(card.dataset.color);
      const lbl = document.createElement('p'); lbl.className = 'cr-lbl'; lbl.textContent = color ? 'Select color' : 'Select ' + op.name.toLowerCase();
      const row = document.createElement('div'); row.className = 'cr-row'; groups.push(row);
      const single = op.values.length === 1; // an option with one value needs no choosing
      if (pre || single) { lbl.style.display = 'none'; row.style.display = 'none'; chosen[i] = pre ? card.dataset.color : op.values[0]; }
      o.appendChild(lbl);
      op.values.forEach((val) => {
        const b = document.createElement('button'); b.type = 'button'; b.dataset.v = val; b.title = val;
        const vv = color && p.variants.find((v) => v['option' + (i + 1)] === val && v.featured_image);
        if (vv) { b.className = 'cr-swatch cr-swatch--lg'; b.style.backgroundImage = 'url(' + sized(vv.featured_image.src, 100) + ')'; b.setAttribute('aria-label', val); }
        else { b.className = 'cr-pill'; b.textContent = val; }
        b.addEventListener('click', () => { chosen[i] = chosen[i] === val ? '' : val; refresh(true); });
        row.appendChild(b);
      });
      o.appendChild(row);
    });
    o.append(msg);
    refresh(true);
  }

  confirm(card) {
    const b = card && card.querySelector('.cr-add'); if (!b) return;
    const o = card.querySelector('.cr-opts'); if (o) o.hidden = true;
    b.textContent = 'Added \u2713'; b.classList.add('is-added');
    setTimeout(() => { b.textContent = 'Quick add'; b.classList.remove('is-added'); }, 1800);
  }

  async add(card, p, variantId, btn, msg) {
    btn.disabled = true; btn.textContent = 'Adding…'; msg.textContent = 'Adding…';
    await eventsReady;
    const E = cartEvents && cartEvents.CartLinesUpdateEvent;
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const deferred = E ? E.createPromise() : null;
    if (E) this.dispatchEvent(new E({ action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise }));
    try {
      const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }) });
      const j = await r.json();
      if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add this item.');
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      // keep the product in the slider; just confirm on its button
      window.__crAddedId = p.id; window.__crAddedAt = Date.now();
      this.confirm(card);
      if (E) deferred.resolve({ cart: E.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'cart-recommendations', sourceId: 'cart-recommendations',
        itemCount: 1, productId: String(p.id), sections: j.sections, didError: false } });
      else window.location.href = '/cart';
    } catch (err) {
      if (deferred) deferred.reject(err);
      btn.disabled = false; btn.textContent = 'Add to bag'; msg.textContent = err.message || 'Could not add this item.';
    }
  }
}
if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);
